import { useState, useEffect, useRef, useMemo } from "react";
import classes from "./Calendar.module.css"
import 'react-datepicker/dist/react-datepicker.css'

import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker"
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useStudioAvailabilities } from "../../hooks/calendar/useStudioAvailabilities";
import { useModalSearchParamHistory } from "../../hooks/useModalSearchParamHistory";
import Modal from "../../components/UI/Modal";
import AvailabilityModal from "./AvailabilityModal";
import BlockTimes from "./BlockTimes";
import { formatBerlinDate, getBerlinTodayDateString, getBerlinTodayForPicker } from "../../util/bookingDateTime";

// Configure DatePicker to use German locale so weeks start on Monday
registerLocale('de', de);
setDefaultLocale('de');

const DatePickerWrapper = ({ children, from }) => (
  <div className={`${classes.datePickerWrapper} ${from === "blockedContainer" ? classes.blockedContainer : ""}`}>{children}</div>
);

export const Calendar = ({ 
    data,
    isLoading,
    error, 
    setSelectedBookingDate, 
    selectedDate, 
    setSelectedDate,
    selectedStudioId, 
    setSelectedStudioId,
    blockedTimes,
    blockedTimesLoading,
    blockedTimesError,
    refetchBlockedTimes,
    createBlockedTime,
    deleteBlockedTime,
    onCalendarSuccess,
  }) => {

  const [showStudioList, setShowStudioList] = useState(false);
  const [showChangeTimePopUp, setShowChangeTimePopUp] = useState(false);
  const [studioImagesReady, setStudioImagesReady] = useState(false);

  const {
    isOpen: showBlockTimeModal,
    openOverlay: openBlockTimeModal,
    closeOverlay: closeBlockTimeModal,
  } = useModalSearchParamHistory({
    paramName: "calendar-hours",
    paramValue: "open",
    resetKey: selectedStudioId,
  });

  const {
    isOpen: showAvailabilityModal,
    openOverlay: openAvailabilityModal,
    closeOverlay: closeAvailabilityModal,
  } = useModalSearchParamHistory({
    paramName: "calendar-block",
    paramValue: "open",
    resetKey: selectedStudioId,
  });

  const { 
    availabilities, 
    loading: availabilitiesLoading, 
    error: availabilitiesError, 
    refetch: refetchAvailabilities,
    createAvailability,
    deleteAvailability
  } = useStudioAvailabilities(selectedStudioId);

  const changeTimePopUpRef = useRef(null);

  const blockedTimesRenderKey = useMemo(() => {
    const items = blockedTimes?.blockedTimesByStudioID?.items ?? [];
    return [selectedStudioId, ...items.map((item) => item?.id).filter(Boolean)].join(":");
  }, [blockedTimes, selectedStudioId]);
  const studiosMap = Object.fromEntries(
    data?.map(studio => [studio.studioId, studio]) || []
  );

  const studioImageUrlsKey = useMemo(() => {
    if (!data?.length) return "[]";
    return JSON.stringify(
      [...new Set(data.map((s) => s?.image).filter(Boolean))].sort()
    );
  }, [data]);

  useEffect(() => {
    if (isLoading) {
      setStudioImagesReady(false);
      return undefined;
    }
    let urls = [];
    try {
      urls = JSON.parse(studioImageUrlsKey);
    } catch {
      urls = [];
    }
    if (!Array.isArray(urls) || urls.length === 0) {
      setStudioImagesReady(true);
      return undefined;
    }
    let cancelled = false;
    setStudioImagesReady(false);
    Promise.all(
      urls.map(
        (url) =>
          new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = url;
          })
      )
    ).then(() => {
      if (!cancelled) setStudioImagesReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading, studioImageUrlsKey]);

  const handleAvailabilityModal = () => {
    setShowChangeTimePopUp(false);
    if (showAvailabilityModal) {
      closeAvailabilityModal();
    } else {
      openAvailabilityModal();
    }
  };

  const handleChangeTimePopUp = () => {
    setShowChangeTimePopUp(!showChangeTimePopUp);
  };

  const handleBlockTimeModal = () => {
    setShowChangeTimePopUp(false);
    if (showBlockTimeModal) {
      closeBlockTimeModal();
    } else {
      openBlockTimeModal();
    }
  };

  const getBookingDots = (date) => {
    if (!selectedStudio?.bookings) return [];
    
    const todayDateStr = getBerlinTodayDateString();

    const dateBookings = selectedStudio.bookings.filter(booking => {
      const [day, month, year] = booking.date.split('.');
      const bookingDateStr = `${year}-${month}-${day}`;
      
      return booking.date === formatBerlinDate(date, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }) &&
             booking.status !== "CANCELLED" &&
             booking.status !== "DECLINED" &&
             bookingDateStr >= todayDateStr;
    });
  
    const colors = {
      'Buchung': '#5ECD90',
      'Anfrage': '#FFA800',
      'Creator-Angebot': '#2776FF'
    };
  
    const totalBookings = dateBookings.length;
    const dotsToShow = Math.min(totalBookings, 5);
    
    const dotGroups = [];
  
    for (let i = 0; i < dotsToShow; i++) {
      let borderRadius;
      
      if (totalBookings === 1) {
        borderRadius = '50%';
      } else if (totalBookings === 2) {
        borderRadius = i === 0 ? '3px 0 0 3px' : '0 3px 3px 0';
      } else {
        if (i === 0) {
          borderRadius = '3px 0 0 3px';
        } else if (i === dotsToShow - 1) {
          borderRadius = '0 3px 3px 0';
        } else {
          borderRadius = '0';
        }
      }
  
      dotGroups.push({
        color: colors[dateBookings[i].type],
        dots: [{borderRadius}]
      });
    }
  
    if (totalBookings > 5) {
      dotGroups.push({
        color: '#808080',
        dots: [{
          borderRadius: '50%',
          marginLeft: '2px'
        }]
      });
    }
        
    return dotGroups;
  };

  const isDateBlocked = (date) => {
    if (!blockedTimes || !blockedTimes.blockedTimesByStudioID || !blockedTimes.blockedTimesByStudioID.items) {
      return false;
    }
    
    const blockedTimeItems = blockedTimes.blockedTimesByStudioID.items;
    
    if (!Array.isArray(blockedTimeItems) || blockedTimeItems.length === 0) {
      return false;
    }
        
    return blockedTimeItems.some(blockedTime => {
      if (!blockedTime || !blockedTime.startDateTime || !blockedTime.endDateTime) {
        return false;
      }
      
      const startDate = new Date(blockedTime.startDateTime);
      const endDate = new Date(blockedTime.endDateTime);
      
      const normalize = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const normalizedDate = normalize(date);
      const normalizedStart = normalize(startDate);
      const normalizedEnd = normalize(endDate);
      
      // Check if the date falls within the blocked period
      return normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd;
    });
  };

  const getBlockedDateIntervals = () => {    
    // Return empty array to allow all dates to be selectable
    // Blocked days will still be visually indicated and handled in the Booking component
    return [];
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (changeTimePopUpRef.current && !changeTimePopUpRef.current.contains(event.target)) {
        setShowChangeTimePopUp(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedStudio = studiosMap[selectedStudioId];

  const handleStudioClick = () => {
    setShowStudioList(!showStudioList);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedBookingDate({
      date: format(date, 'dd.MM.yyyy'),
      studioId: selectedStudioId
    });
  };

  const handleStudioSelect = (studioId) => {
    setSelectedStudioId(studioId);
    setShowStudioList(false);
  };

  const showStudioHeaderSkeleton =
    isLoading || (Boolean(data?.length) && !studioImagesReady);

  const renderDayContents = (day, date) => {
    const dotGroups = getBookingDots(date);
    const isBlocked = isDateBlocked(date);
      
    return (
      <div className={`${classes.dayContainer} ${isBlocked ? classes.blockedDay : ''}`}>
        <span>{format(date, 'd')}</span>
        {isBlocked && (
          <div className={classes.blockedIndicator}>
          </div>
        )}
        {dotGroups.length > 0 && (
          <div className={classes.bookingDots}>
            {dotGroups.map((group, groupIndex) => (
              <div key={groupIndex} className={classes.dotGroup}>
                {group.dots.map((dotStyle, dotIndex) => (
                  <div 
                    key={`${groupIndex}-${dotIndex}`}
                    className={classes.dot}
                    style={{ 
                      backgroundColor: group.color,
                      ...dotStyle
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  const LoadingDots = () => {
    return (
      <div className={classes.loadingContainer}>
        <div className={classes.loadingSpinner}></div>
      </div>
    );
  };

  return (
    <div className={classes.outsideContainer}>
      <div className={classes.header}>
          <div className={classes.headerContent}>
            {showStudioHeaderSkeleton ? (
              <>
                <div className={classes.studioImageSkeleton}></div>
                <div className={classes.studioNameSkeleton}></div>
              </>
            ) : (
              <>
                <div className={classes.mobileHeader}>  
                  <div className={classes.studioMobileImage}>
                    <img src={selectedStudio?.image} alt="Studio Image" loading="eager" decoding="async" />
                  </div>
                  <div className={classes.studioMobileName} onClick={handleStudioClick}>
                    <h2 className="mediumLarge">{selectedStudio?.name}</h2>
                    <button className={`${classes.arrowDownButton} ${showStudioList ? classes.arrowUp : ''}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                        <line x1="0.353553" y1="0.646447" x2="5.68167" y2="5.97456" stroke="black"/>
                        <line x1="4.98898" y1="5.96072" x2="10.7611" y2="0.63261" stroke="black"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className={classes.studioImage}>
                    <img src={selectedStudio?.image} alt="Studio Image" loading="eager" decoding="async" />
                </div>
                <div className={classes.studioName} onClick={handleStudioClick}>
                    <h2 className="titleSemibold3">{selectedStudio?.name}</h2>
                    <button className={`${classes.arrowDownButton} ${showStudioList ? classes.arrowUp : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                            <line x1="0.353553" y1="0.646447" x2="5.68167" y2="5.97456" stroke="black"/>
                            <line x1="4.98898" y1="5.96072" x2="10.7611" y2="0.63261" stroke="black"/>
                        </svg>
                    </button>
                  </div>
                </>
                )}
                {showStudioList && !showStudioHeaderSkeleton && (
                <div className={classes.studioDropdown}>
                {Object.values(studiosMap).map((studio) => (
                <div
                  key={studio.studioId}
                  className={classes.studioOption}
                  onClick={() => handleStudioSelect(studio.studioId)}
                >
                  <img src={studio.image} alt={studio.name} className={classes.dropdownStudioImage} loading="eager" decoding="async" />
                  <div className={classes.studioInfo}>
                    <span className="titleSemibold3">{studio.name}</span>
                    <span className="regularXs">{studio.location}</span>
                  </div>
                </div>
                ))}
              </div>
              )}
          </div>
          <div className={classes.changeTimePopUpContainer} ref={changeTimePopUpRef}>
            <button className={`${classes.buttonTimeChangeText} semiboldXs`} onClick={handleChangeTimePopUp}>Zeiten verwalten</button>
            {showChangeTimePopUp && (
                <div className={classes.changeTimePopUp}>
                  <div onClick={handleAvailabilityModal} className={classes.changeTimePopUpText}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                      <path d="M16.7607 17.1497H1.35108C0.607834 17.1497 0 16.5424 0 15.7931V2.43863C0 1.68933 0.607834 1.08203 1.35108 1.08203H16.7607C17.5039 1.08203 18.1117 1.69235 18.1117 2.43863V15.7931C18.1117 16.5424 17.5039 17.1497 16.7607 17.1497ZM1.35108 2.08211C1.15549 2.08211 0.99601 2.24224 0.99601 2.43863V15.7931C0.99601 15.9895 1.15549 16.1496 1.35108 16.1496H16.7607C16.9563 16.1496 17.1157 15.9895 17.1157 15.7931V2.43863C17.1157 2.24224 16.9563 2.08211 16.7607 2.08211H1.35108Z" fill="#1F1F1F"/>
                      <path d="M9.5507 0H8.55469V3.0667H9.5507V0Z" fill="#1F1F1F"/>
                      <path d="M13.8046 0H12.8086V3.0667H13.8046V0Z" fill="#1F1F1F"/>
                      <path d="M5.31632 0H4.32031V3.0667H5.31632V0Z" fill="#1F1F1F"/>
                      <path d="M19.223 12.9211C19.223 15.7273 16.9477 18.0024 14.1408 18.0024C11.3339 18.0024 9.05859 15.7273 9.05859 12.9211C9.05859 10.1149 11.3339 7.83984 14.1408 7.83984C16.9477 7.83984 19.223 10.1149 19.223 12.9211Z" fill="white" stroke="#1F1F1F"/>
                      <line y1="-0.5" x2="10.2583" y2="-0.5" transform="matrix(0.707166 -0.707048 0.707166 0.707048 10.8594 16.6836)" stroke="#1F1F1F"/>
                      </svg>
                      <span className="regularSmall">Tage oder Zeit blockieren</span>
                  </div>
                  <div className={classes.changeTimePopUpText} onClick={handleBlockTimeModal}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M9.25 1.50391C4.69365 1.50391 1 5.19756 1 9.75391C1 14.3103 4.69365 18.0039 9.25 18.0039C13.8063 18.0039 17.5 14.3103 17.5 9.75391C17.5 5.19756 13.8063 1.50391 9.25 1.50391ZM0 9.75391C0 4.64527 4.14137 0.503906 9.25 0.503906C14.3586 0.503906 18.5 4.64527 18.5 9.75391C18.5 14.8625 14.3586 19.0039 9.25 19.0039C4.14137 19.0039 0 14.8625 0 9.75391ZM8.38623 10.802V4.50241H9.38623V10.2121L13.5705 12.4629L13.0968 13.3436L8.43013 10.8333L8.447 10.802H8.38623Z" fill="#1F1F1F"/>
                    </svg>
                    <span className="regularSmall">Betriebszeiten verwalten</span>
                  </div>
                </div>
            )}
          </div>
      </div>
      <div className={classes.weekContainer}> 
        <div className={classes.weekDay}>
          <span>Mo.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Di.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Mi.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Do.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Fr.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Sa.</span>
        </div>
        <div className={classes.weekDay}>
          <span>So.</span>
        </div>
      </div>
      <div className={classes.container}>
        {isLoading ? (
          <LoadingDots />
        ) : error ? (
          <div>Kalender konnte nicht geladen werden.</div>
        ) : (
          <DatePickerWrapper>
            <DatePicker
              key={blockedTimesRenderKey}
              dateFormat="dd.MM.yyyy"
              inline
              locale="de"
              minDate={getBerlinTodayForPicker()}
              monthsShown={12}
              renderDayContents={renderDayContents}
              onChange={handleDateSelect}
              selected={selectedDate}
              excludeDateIntervals={getBlockedDateIntervals()}
            />
          </DatePickerWrapper>
        )}
      </div>
      <footer className={classes.footer}>
        <div className={classes.explanationContainer}>
            <div style={{backgroundColor: "#5ECD90"}} className={classes.dot}></div>
            <span className="regularXs">Buchung/en</span>
        </div>
        <div className={classes.explanationContainer}>
            <div style={{backgroundColor: "#FFA800"}} className={classes.dot}></div>
            <span className="regularXs">Anfrage/n</span>
        </div>
        <div className={classes.explanationContainer}>
            <div style={{backgroundColor: "#2776FF"}} className={classes.dot}></div>
            <span className="regularXs">Anbieter-Angebot</span>
        </div>
      </footer>
      <Modal
        className="calendarHoursSheet"
        open={showBlockTimeModal}
        onBackdropClick={closeBlockTimeModal}
      >
        <div className={classes.blockTimeModalContainer}>
          <header className={classes.blockTimeModalHeader}>
            <h2 style={{minHeight: "50px", display: "flex"}} className="titleSemibold3">Betriebszeiten verwalten</h2>
            <div className={classes.closeButton} onClick={closeAvailabilityModal}>
              <svg onClick={closeBlockTimeModal} xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 9L0 1.49054L0.990535 0.5L8.5 8.00947L16.0095 0.5L17 1.49054L9.49054 9L17 16.5095L16.0095 17.5L8.5 9.99054L0.990535 17.5L0 16.5095L7.50947 9Z" fill="#1F1F1F"/>
              </svg>
            </div>
          </header>
          <div className={classes.blockTimeModalTextContainer}>
            <span className={`${classes.blockTimeModalText} regularXs`}>
              Aktive Buchungen bleiben bei Änderungen der Betriebszeiten bestehen und müssen separat storniert werden.
            </span>
          </div>
          {showBlockTimeModal && 
          <AvailabilityModal 
            from="calendarPage"
            bookingData={selectedStudio?.bookings?.filter(booking => booking.status !== "CANCELLED" && booking.status !== "DECLINED")}
            minNumberOfHours={selectedStudio?.minNumberOfHours}
            key={selectedStudioId} 
            studioData={availabilities} 
            studioId={selectedStudioId} 
            onSave={() => {
              closeBlockTimeModal();
              if (typeof refetchAvailabilities === 'function') {
                refetchAvailabilities();
              }
              onCalendarSuccess?.("Betriebszeiten gespeichert");
            }} 
            createAvailability={createAvailability} 
            deleteAvailability={deleteAvailability} />}
          </div>
      </Modal>
      <Modal
        className="calendarBlockSheet"
        open={showAvailabilityModal}
        onBackdropClick={closeAvailabilityModal}
      >
        <div className={classes.availabilityModalContainer}>
          <header className={classes.blockTimeModalHeader}>
            <h2 className={`titleSemibold3 ${classes.blockTimeHeaderTitle}`}>Tage oder Zeit blockieren</h2>
            <h2 className={`titleSemibold3 ${classes.blockTimeHeaderTitlePhone}`}>Zeit blockieren</h2>
            <div className={classes.closeButton} onClick={closeAvailabilityModal}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 9L0 1.49054L0.990535 0.5L8.5 8.00947L16.0095 0.5L17 1.49054L9.49054 9L17 16.5095L16.0095 17.5L8.5 9.99054L0.990535 17.5L0 16.5095L7.50947 9Z" fill="#1F1F1F"/>
              </svg>
            </div>
          </header>
          <div className={classes.availabilityModalTextContainer}>
            <BlockTimes 
              blockedTimes={blockedTimes}
              createBlockedTime={createBlockedTime}
              deleteBlockedTime={deleteBlockedTime}
              loading={blockedTimesLoading}
              error={blockedTimesError}
              refetchBlockedTimes={refetchBlockedTimes}
              studioData={availabilities}
              studioId={selectedStudioId}
              bookingData={selectedStudio?.bookings?.filter(booking => booking.status !== "CANCELLED" && booking.status !== "DECLINED")}
              onSave={() => {
                if (typeof refetchAvailabilities === 'function') {
                  refetchAvailabilities();
                }
                closeAvailabilityModal();
                onCalendarSuccess?.("Blockierte Zeiten gespeichert");
              }} 
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Calendar;
